import { UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  notification,
  Row,
  Select,
  Typography,
  Upload,
} from "antd";
import {
  IMPORT_CNF_BILL_EDIT_ENDPOINT,
  IMPORT_CNF_BILL_NEXT_BILL_NO_ENDPOINT,
  IMPORT_CNF_BILL_STORE_ENDPOINT,
  IMPORT_CNF_EXPENDITURE_LIST_ENDPOINT,
  IMPORT_DETAIL_CNF_LIST,
  SALES_CONTRACT_LIST,
} from "apiServices/API_ENDPOINTS";
import { getData, postData } from "apiServices/common";
import ExpenditureParticularsTable from "components/ImportCustomsClearance/ExternalCnfBills/ExpenditureParticularsTable";
import { computeImportAgencyCommissionTaka } from "components/ImportCustomsClearance/ExternalCnfBills/agencyCommission";
import { formatGoodsDescriptionDisplay } from "components/ImportCustomsClearance/ExternalCnfBills/goodsDescription";
import debounce from "lodash/debounce";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const { Text } = Typography;

function buildExpenditureOptions(rows) {
  return (rows || []).map((e) => ({
    value: e.id,
    label: e.name || `ID ${e.id}`,
    amount:
      e.amount !== undefined && e.amount !== null ? Number(e.amount) : undefined,
  }));
}

export default function ImportCnfBillForm({
  form,
  isEdit,
  editInfo,
  viewMode,
  canEditInvoice = true,
  billId,
  onSubmittingChange,
}) {
  const navigate = useNavigate();
  const submittingRef = useRef(false);
  const [importInvoiceOptions, setImportInvoiceOptions] = useState([]);
  const [salesContractOptions, setSalesContractOptions] = useState([]);
  const [expenditureRows, setExpenditureRows] = useState([]);
  const [loadingInvSearch, setLoadingInvSearch] = useState(false);
  const [loadingScSearch, setLoadingScSearch] = useState(false);
  const [loadingExp, setLoadingExp] = useState(false);
  const [selectedImport, setSelectedImport] = useState(null);
  /** Controlled search text so typing always drives remote API (antd Select + showSearch). */
  const [invoiceSelectSearch, setInvoiceSelectSearch] = useState(undefined);
  const [documentFileList, setDocumentFileList] = useState([]);

  const invoiceNumber = Form.useWatch("invoice_number", form);
  const conversionRate = Form.useWatch("conversion_rate", form);

  const searchImportInvoices = useCallback(async (searchText) => {
    setLoadingInvSearch(true);
    const body = {
      page: 1,
      per_page: 50,
      exclude_used_in_cnf_bill: 1,
      ...(isEdit && billId ? { cnf_bill_id: billId } : {}),
      ...(searchText && String(searchText).trim()
        ? { invoice_number: String(searchText).trim() }
        : {}),
    };
    const res = await getData(IMPORT_DETAIL_CNF_LIST, false, body);
    if (res?.status === 200) {
      const rows = res?.data?.data?.data || [];
      setImportInvoiceOptions(
        (Array.isArray(rows) ? rows : []).map((inv) => ({
          value: inv.id,
          label: inv.invoice_number || `Invoice #${inv.id}`,
          invoice: inv,
        })),
      );
    } else {
      setImportInvoiceOptions([]);
    }
    setLoadingInvSearch(false);
  }, [isEdit, billId]);

  const searchImportInvoicesRef = useRef(searchImportInvoices);
  searchImportInvoicesRef.current = searchImportInvoices;
  const debouncedInvoiceSearch = useMemo(
    () =>
      debounce((t) => {
        searchImportInvoicesRef.current(t);
      }, 300),
    [],
  );

  const searchSalesContracts = useCallback(async (searchText) => {
    setLoadingScSearch(true);
    const body = {
      page: 1,
      per_page: 50,
      ...(searchText && String(searchText).trim()
        ? { reference_no: String(searchText).trim() }
        : {}),
    };
    const res = await getData(SALES_CONTRACT_LIST, false, body);
    if (res?.status === 200) {
      const rows = res?.data?.data?.data || [];
      setSalesContractOptions(
        (Array.isArray(rows) ? rows : []).map((sc) => ({
          value: sc.id,
          label: sc.reference_no || sc.sales_contract_unique_id || `SC ${sc.id}`,
        })),
      );
    } else {
      setSalesContractOptions([]);
    }
    setLoadingScSearch(false);
  }, []);

  const debouncedScSearch = useMemo(
    () => debounce((t) => searchSalesContracts(t), 400),
    [searchSalesContracts],
  );

  const fetchExpenditureSettings = useCallback(async () => {
    setLoadingExp(true);
    const res = await getData(IMPORT_CNF_EXPENDITURE_LIST_ENDPOINT, false, {
      per_page: 500,
      page: 1,
    });
    if (res?.status === 200) {
      const rows = res?.data?.data?.data || [];
      setExpenditureRows(Array.isArray(rows) ? rows : []);
    }
    setLoadingExp(false);
  }, []);

  useEffect(() => {
    fetchExpenditureSettings();
  }, [fetchExpenditureSettings]);

  useEffect(() => {
    searchImportInvoices("");
  }, [searchImportInvoices]);

  useEffect(() => {
    searchSalesContracts("");
  }, [searchSalesContracts]);

  useEffect(() => {
    return () => {
      debouncedInvoiceSearch.cancel();
      debouncedScSearch.cancel();
    };
  }, [debouncedInvoiceSearch, debouncedScSearch]);

  useEffect(() => {
    if (!invoiceNumber) {
      setSelectedImport(null);
      return;
    }
    const opt = importInvoiceOptions.find((o) => o.value === invoiceNumber);
    if (opt?.invoice) {
      setSelectedImport(opt.invoice);
      return;
    }
    setSelectedImport(null);
  }, [invoiceNumber, importInvoiceOptions]);

  useEffect(() => {
    const invVal = selectedImport?.invoice_value;
    const cr = conversionRate;
    if (invVal == null || cr == null || cr === "") {
      form.setFieldsValue({ agency_commission: undefined });
      return;
    }
    form.setFieldsValue({
      agency_commission: computeImportAgencyCommissionTaka(invVal, cr),
    });
  }, [selectedImport, conversionRate, form]);

  const mergeSalesContractFromInvoice = useCallback((inv) => {
    if (!inv) return;
    const rawId = inv.sales_contract_id;
    if (rawId == null || rawId === "") return;

    const scId = Number(rawId);
    const label =
      (inv.sales_contract_number != null &&
        String(inv.sales_contract_number).trim()) ||
      inv.sales_contract_info?.reference_no ||
      inv.sale_contract_info?.reference_no ||
      `SC ${scId}`;

    form.setFieldsValue({ sales_contract_id: scId });

    setSalesContractOptions((prev) => {
      if (prev.some((s) => s.value === scId)) return prev;
      return [{ value: scId, label: String(label) }, ...prev];
    });
  }, [form]);

  const onInvoiceChange = (id) => {
    setInvoiceSelectSearch(undefined);
    const opt = importInvoiceOptions.find((o) => o.value === id);
    const inv = opt?.invoice;
    mergeSalesContractFromInvoice(inv);
  };

  const mergeBillDetailsFromApi = useCallback((detailsFromApi) => {
    return (detailsFromApi || []).map((d) => ({
      expenditure_id: d.expenditure_id,
      amount:
        d.amount !== undefined && d.amount !== null ? Number(d.amount) : 0,
    }));
  }, []);

  useEffect(() => {
    if (!isEdit || !editInfo) return;

    const invId = editInfo.invoice_number;
    const inv = editInfo.import_invoice || null;

    if (inv) {
      setImportInvoiceOptions((prev) => {
        if (prev.some((p) => p.value === inv.id)) return prev;
        return [
          {
            value: inv.id,
            label: inv.invoice_number || `Invoice #${inv.id}`,
            invoice: inv,
          },
          ...prev,
        ];
      });
      setSelectedImport(inv);
    }

    const billDetails = mergeBillDetailsFromApi(editInfo.bill_details);
    const scId =
      inv?.sales_contract_id != null && inv?.sales_contract_id !== ""
        ? Number(inv.sales_contract_id)
        : undefined;

    setDocumentFileList(
      editInfo.document
        ? [
            {
              uid: "-1",
              name: editInfo.document.split("/").pop(),
              status: "done",
              url: `${process.env.REACT_APP_BASE_URL}/${editInfo.document}`,
            },
          ]
        : [],
    );

    form.setFieldsValue({
      bill_no: editInfo.bill_no,
      invoice_number: invId,
      sales_contract_id: scId,
      bill_of_entry_no: editInfo.bill_of_entry_no,
      documents_received_date: editInfo.documents_received_date
        ? moment(editInfo.documents_received_date)
        : null,
      bill_of_entry_date: editInfo.bill_of_entry_date
        ? moment(editInfo.bill_of_entry_date)
        : null,
      conversion_rate: editInfo.conversion_rate
        ? Number(editInfo.conversion_rate)
        : undefined,
      agency_commission: editInfo.agency_commission
        ? Number(editInfo.agency_commission)
        : undefined,
      bill_details:
        billDetails.length > 0
          ? billDetails
          : [{ expenditure_id: undefined, amount: undefined }],
    });

    if (inv) {
      mergeSalesContractFromInvoice(inv);
    }
  }, [isEdit, editInfo, form, mergeBillDetailsFromApi, mergeSalesContractFromInvoice]);

  useEffect(() => {
    if (isEdit) return;
    form.setFieldsValue({
      bill_details: [{ expenditure_id: undefined, amount: undefined }],
    });

    const fetchNextBillNo = async () => {
      const res = await getData(IMPORT_CNF_BILL_NEXT_BILL_NO_ENDPOINT);
      if (res?.status === 200) {
        form.setFieldsValue({ bill_no: res?.data?.data?.bill_no });
      }
    };
    fetchNextBillNo();
  }, [isEdit, form]);

  const formatImportDisplayDate = (value) =>
    value ? moment(value).format("YYYY-MM-DD") : "";

  const display = useMemo(() => {
    const im = selectedImport;
    if (!im) {
      return {
        invoiceValue: "",
        qty: "",
        lc: "",
        eta: "",
        berthing: "",
        delivery: "",
        goods: "",
      };
    }
    return {
      invoiceValue: im.invoice_value ?? "",
      qty: im.quantity_ctn ?? "",
      lc: im.b2blc_info?.b2b_lc_number ?? "",
      eta: formatImportDisplayDate(im.eta),
      berthing: formatImportDisplayDate(im.berthing_date),
      delivery: formatImportDisplayDate(im.delivery_date),
      goods: formatGoodsDescriptionDisplay(im.goods_description),
    };
  }, [selectedImport]);

  const expenditureOptions = useMemo(
    () => buildExpenditureOptions(expenditureRows),
    [expenditureRows],
  );

  const onFinish = async (values) => {
    if (submittingRef.current) return;

    const bill_details = (values.bill_details || [])
      .filter(
        (row) =>
          row &&
          row.expenditure_id != null &&
          row.amount !== undefined &&
          row.amount !== null,
      )
      .map((row) => ({
        expenditure_id: row.expenditure_id,
        amount: Number(row.amount) || 0,
      }));

    if (!bill_details.length) {
      notification.error({
        message: "Validation",
        description: "Add at least one expenditure row with type and amount.",
        placement: "topRight",
      });
      return;
    }

    const agencyCommission =
      values.agency_commission ??
      computeImportAgencyCommissionTaka(
        selectedImport?.invoice_value,
        values.conversion_rate,
      );

    const payload = new FormData();
    payload.append("invoice_number", values.invoice_number);
    payload.append("bill_of_entry_no", values.bill_of_entry_no || "");
    payload.append(
      "documents_received_date",
      values.documents_received_date
        ? values.documents_received_date.format("YYYY-MM-DD")
        : "",
    );
    payload.append(
      "bill_of_entry_date",
      values.bill_of_entry_date
        ? values.bill_of_entry_date.format("YYYY-MM-DD")
        : "",
    );
    payload.append("conversion_rate", values.conversion_rate ?? "");
    payload.append("agency_commission", agencyCommission ?? "");
    payload.append("bill_details", JSON.stringify(bill_details));
    if (documentFileList[0]?.originFileObj) {
      payload.append("document", documentFileList[0].originFileObj);
    }

    submittingRef.current = true;
    onSubmittingChange?.(true);
    try {
      if (isEdit && billId) {
        // PHP does not parse multipart bodies on PUT, so spoof the method over POST.
        payload.append("_method", "PUT");
        const res = await postData(
          `${IMPORT_CNF_BILL_EDIT_ENDPOINT}/${billId}`,
          payload,
        );
        if (res && (res?.code === 200 || res?.status === 200)) {
          notification.success({
            message: "Success",
            description: res?.message || "Updated",
            placement: "topRight",
          });
          navigate(
            "/import-export-expense-tracking/external-cnf-bills/import-cnf-bill",
          );
        }
      } else {
        const res = await postData(IMPORT_CNF_BILL_STORE_ENDPOINT, payload);
        if (res && (res?.code === 200 || res?.status === 200)) {
          notification.success({
            message: "Success",
            description: res?.message || "Created",
            placement: "topRight",
          });
          navigate(
            "/import-export-expense-tracking/external-cnf-bills/import-cnf-bill",
          );
        }
      }
    } finally {
      submittingRef.current = false;
      onSubmittingChange?.(false);
    }
  };

  const disabled = !!viewMode;
  const invoiceDisabled = disabled || (isEdit && !canEditInvoice);

  return (
    <Card loading={loadingExp}>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={16}>
          <Col xs={24} md={4}>
            <Form.Item name="bill_no" label="Bill Number">
              <Input readOnly disabled placeholder="Loading..." />
            </Form.Item>
          </Col>
        </Row>
        <Text strong>Commercial invoice</Text>
        <Row gutter={16}>
          <Col xs={24} md={4}>
            <Form.Item
              name="invoice_number"
              label="Commercial Invoice Number"
              tooltip="All Import Invoices"
              rules={[{ required: true, message: "Required" }]}
            >
              <Select
                showSearch
                placeholder="All Import Invoices — type to search"
                loading={loadingInvSearch}
                disabled={invoiceDisabled}
                filterOption={false}
                defaultActiveFirstOption={false}
                optionFilterProp="label"
                searchValue={invoiceSelectSearch}
                onSearch={(text) => {
                  setInvoiceSelectSearch(text);
                  debouncedInvoiceSearch(text);
                }}
                onChange={onInvoiceChange}
                allowClear
                onClear={() => {
                  setInvoiceSelectSearch(undefined);
                  setSelectedImport(null);
                  form.setFieldsValue({ sales_contract_id: undefined });
                }}
                options={importInvoiceOptions.map(({ value, label }) => ({
                  value,
                  label,
                }))}
                onDropdownVisibleChange={(open) => {
                  if (open && importInvoiceOptions.length === 0) {
                    searchImportInvoices("");
                  }
                  if (!open) {
                    setInvoiceSelectSearch(undefined);
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item
              name="sales_contract_id"
              label="Sales Contract Number"
              // tooltip="Search sales contracts (not stored on bill API)"
            >
              <Select
                showSearch
                placeholder="Search by reference no."
                loading={loadingScSearch}
                disabled={disabled}
                filterOption={false}
                onSearch={(t) => debouncedScSearch(t)}
                options={salesContractOptions}
                allowClear
                onDropdownVisibleChange={(open) => {
                  if (open && salesContractOptions.length === 0) {
                    searchSalesContracts("");
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item label="Invoice Value">
              <Input readOnly disabled value={display.invoiceValue} />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item label="Invoice Quantity">
              <Input readOnly disabled value={display.qty} />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item label="LC Number">
              <Input readOnly disabled value={display.lc} />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item
              label="ETA Date"
              tooltip="Auto (Non Editable) — from import invoice"
            >
              <Input readOnly disabled value={display.eta} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={4}>
            <Form.Item
              label="Berthing Date"
              tooltip="Auto (Non Editable) — from import invoice"
            >
              <Input readOnly disabled value={display.berthing} />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item
              label="Delivery Date"
              tooltip="Auto (Non Editable) — from import invoice"
            >
              <Input readOnly disabled value={display.delivery} />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item
              label="Description of Goods"
              // tooltip="Auto (Non Editable) — from import invoice"
            >
              <Input readOnly disabled value={display.goods} />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item
              name="documents_received_date"
              label="Documents Received Date"
            >
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                disabled={disabled}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item name="bill_of_entry_no" label="Bill of Entry no">
              <Input disabled={disabled} />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item name="bill_of_entry_date" label="Bill of Entry Date">
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                disabled={disabled}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={4}>
            <Form.Item
              name="conversion_rate"
              label="Conversion Rate"
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                disabled={disabled}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item name="agency_commission" label="Agency Commission (Taka)">
              <InputNumber style={{ width: "100%" }} readOnly disabled />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Document (PDF)">
              <Upload
                accept=".pdf,application/pdf"
                maxCount={1}
                fileList={documentFileList}
                disabled={disabled}
                showUploadList={{ showRemoveIcon: !disabled }}
                beforeUpload={(file) => {
                  const isPdf =
                    file.type === "application/pdf" ||
                    file.name?.toLowerCase().endsWith(".pdf");
                  if (!isPdf) {
                    notification.error({
                      message: "Invalid file",
                      description: "Only PDF files are allowed.",
                      placement: "topRight",
                    });
                    return Upload.LIST_IGNORE;
                  }
                  return false;
                }}
                onChange={({ fileList }) => setDocumentFileList(fileList.slice(-1))}
              >
                {!disabled && (
                  <Button icon={<UploadOutlined />}>
                    {documentFileList.length > 0 ? "Change PDF" : "Upload PDF"}
                  </Button>
                )}
              </Upload>
            </Form.Item>
          </Col>
        </Row>

        <Text strong style={{ display: "block", marginBottom: 8 }}>
          Particulars of Expenditure (Import C&F settings)
        </Text>
        <ExpenditureParticularsTable
          expenditureOptions={expenditureOptions}
          disabled={disabled}
          loadingOptions={loadingExp}
          showTotalSummary
        />
      </Form>
    </Card>
  );
}
